import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';

import type { App } from '@keboola/api-client/developerPortal/types';
import {
  AlertModal,
  Badge,
  Button,
  EmptyState,
  Modal,
  PageHeader,
  PageLayout,
  PageToolbar,
  SearchInput,
  Skeleton,
  Table,
  toast,
} from '@keboola/design';
import { Info, Plus, Upload } from '@keboola/design/icons';

import { useCurrentVendor } from '../../auth/context';
import { QueryError } from '../../components/QueryError';
import { AppIcon } from '../../features/apps/components/AppIcon';
import { getComponentTypeLabel } from '../../features/apps/helpers';
import { sortApps } from '../../features/apps/vendorHelpers';
import { getApiErrorMessage } from '../../features/auth/errors';
import { DeprecateAppModal } from '../../features/vendors/components/DeprecateAppModal';
import { useDeleteApp, useVendorApps } from '../../features/vendors/queries';

export const Route = createFileRoute('/~/')({
  component: VendorPage,
  head: () => ({ meta: [{ title: 'Components | Keboola Developer Portal' }] }),
});

/** Publishing state, which is orthogonal to public/private and shown as its own column. */
function PublishingStatus({ app }: { app: App }) {
  if (app.publishingStatus === 'requested') {
    return <Badge color="amber">Waiting for publishing</Badge>;
  }

  if (app.publishingStatus === 'rejected') {
    const reason = app.publishingRequest?.rejectionReason;
    return (
      <span className="tw:inline-flex tw:items-center tw:gap-1.5">
        <Badge color="red">Rejected</Badge>
        {typeof reason === 'string' &&
          reason !== '' && (
            // `Modal`, not `AlertModal`: there is nothing to confirm here. `AlertModal` owns a confirm
            // button it always renders, so an info-only use of it shows a second action labelled
            // "Confirm" that does nothing. `Modal.Header` brings its own close icon.
            <Modal>
              <Modal.Trigger asChild>
                {/* The reason is the actionable part of a rejection, so it needs to be reachable —
                    but it can be long, hence a dialog rather than an inline cell. */}
                <Button variant="invisible" size="small" aria-label="Why was this rejected?">
                  <Info />
                </Button>
              </Modal.Trigger>
              {/* Default `medium`, not `small`: a rejection reason is free-form prose an admin types,
                  so 480px would wrap it hard. */}
              <Modal.Content>
                <Modal.Header>Why this component was rejected</Modal.Header>
                <Modal.Body>{reason}</Modal.Body>
              </Modal.Content>
            </Modal>
          )}
      </span>
    );
  }

  return null;
}

/** Visibility, which the original renders as its own colour-coded cell. */
function VisibilityStatus({ app }: { app: App }) {
  if (app.isPublic === true) return <Badge color="green-inverse">Public</Badge>;
  if (app.isDeprecated === true) {
    return (
      <span className="tw:inline-flex tw:flex-col tw:gap-0.5">
        <Badge color="red">Deprecated</Badge>
        {typeof app.expiredOn === 'string' && app.expiredOn !== '' && (
          <span className="tw:text-xs tw:text-muted-foreground">{app.expiredOn}</span>
        )}
      </span>
    );
  }
  return <Badge color="gray-inverse">Private</Badge>;
}

function VendorPage() {
  const vendorId = useCurrentVendor();
  const { data: apps, isLoading, isError, error, refetch } = useVendorApps(vendorId);
  const deleteApp = useDeleteApp(vendorId);
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const sorted = sortApps(apps ?? []);
    const query = search.trim().toLowerCase();
    if (query === '') return sorted;
    // Same fields the original's debounced filter searched.
    return sorted.filter((app) =>
      [app.id, app.name].some((value) => value.toLowerCase().includes(query)),
    );
  }, [apps, search]);

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageHeader.Index
          title="Components"
          description="The components your vendor maintains."
          action={
            <Button asChild variant="primary">
              <Link to="/~/components/new">
                <Plus />
                Add a component
              </Link>
            </Button>
          }
        />
      </PageLayout.Header>

      {isLoading && <Skeleton className="tw:h-64" />}

      {/* Blocking only when there is nothing to show. TanStack Query keeps the previous `data` when a
          refetch fails, so `isError` and a full table are true at the same time — and a failed refresh
          must not take a readable list away from someone mid-task.
          `as="h2"`: this sits directly under the page header's `h1`. */}
      <QueryError
        isError={isError}
        hasData={apps !== undefined}
        error={error}
        onRetry={() => void refetch()}
        loadTitle="Could not load your components"
        refreshTitle="Could not refresh your components"
        staleNote="Showing the components from the last successful load."
        titleAs="h2"
      />

      {apps !== undefined && apps.length === 0 && (
        <EmptyState>
          <EmptyState.Icon>
            <Upload />
          </EmptyState.Icon>
          <EmptyState.Description>You have not registered a component yet.</EmptyState.Description>
          <EmptyState.Actions>
            <Button asChild variant="primary">
              <Link to="/~/components/new">Add your first component</Link>
            </Button>
          </EmptyState.Actions>
        </EmptyState>
      )}

      {apps !== undefined && apps.length > 0 && (
        <>
          <PageToolbar>
            <PageToolbar.Search>
              <SearchInput
                allowClear
                placeholder="Search by name or ID"
                value={search}
                onValueChange={setSearch}
              />
            </PageToolbar.Search>
          </PageToolbar>

          {visible.length === 0 ? (
            <EmptyState variant="compact">
              <EmptyState.Description>No component matches “{search}”.</EmptyState.Description>
            </EmptyState>
          ) : (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>Component</Table.Head>
                  <Table.Head>Type</Table.Head>
                  <Table.Head>Version</Table.Head>
                  <Table.Head>Publishing</Table.Head>
                  <Table.Head>Visibility</Table.Head>
                  <Table.Head>
                    <span className="tw:sr-only">Actions</span>
                  </Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {visible.map((app) => (
                  <Table.Row key={app.id}>
                    <Table.Cell>
                      <div className="tw:flex tw:items-center tw:gap-3">
                        <AppIcon app={app} size={48} />
                        <div className="tw:flex tw:flex-col">
                          <Link
                            to="/~/components/$appId"
                            params={{ appId: app.id }}
                            className="tw:font-medium"
                          >
                            {app.name}
                          </Link>
                          <span className="tw:text-xs tw:text-muted-foreground">{app.id}</span>
                          {app.createdBy !== undefined && (
                            <span className="tw:text-xs tw:text-muted-foreground">
                              added by {app.createdBy}
                            </span>
                          )}
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell>{getComponentTypeLabel(app.type)}</Table.Cell>
                    <Table.Cell>{app.version ?? '—'}</Table.Cell>
                    <Table.Cell>
                      <PublishingStatus app={app} />
                    </Table.Cell>
                    <Table.Cell>
                      <VisibilityStatus app={app} />
                    </Table.Cell>
                    <Table.Cell>
                      <div className="tw:flex tw:justify-end tw:gap-2">
                        <Button asChild variant="secondary" size="small">
                          <Link to="/~/components/$appId/edit" params={{ appId: app.id }}>
                            Edit
                          </Link>
                        </Button>
                        {/* `AlertModal`, never a hand-rolled confirm: it owns the destructive variant and
                            locks itself while the promise is in flight.
                            The `try` is not optional: `AlertModal` settles the handler with `.finally()`
                            and closes on rejection too, so an uncaught failure would look exactly like a
                            successful delete while the component stayed in the list. */}
                        {/* Hidden once it is already deprecated: the call is not idempotent in any
                            useful sense — it would just restamp `expiredOn` — and there is no way back
                            from this screen, so offering it again reads as a toggle it is not. */}
                        {app.isDeprecated !== true && (
                          <DeprecateAppModal app={app} vendorId={vendorId} />
                        )}
                        <AlertModal
                          onConfirm={async () => {
                            try {
                              await deleteApp.mutateAsync(app.id);
                              toast.success(`${app.name} was removed.`);
                            } catch (error) {
                              toast.error(
                                getApiErrorMessage(error, `Could not remove ${app.name}.`),
                              );
                            }
                          }}
                        >
                          <AlertModal.Trigger asChild>
                            <Button variant="danger" size="small">
                              Remove
                            </Button>
                          </AlertModal.Trigger>
                          <AlertModal.Content
                            variant="danger"
                            title={`Remove ${app.name}?`}
                            confirmLabel="Remove component"
                          >
                            This cannot be undone. Any project using this component will stop being
                            able to run it.
                          </AlertModal.Content>
                        </AlertModal>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </>
      )}
    </PageLayout>
  );
}
